// CertificatePage.jsx
// ✅ Naya flow: ?token=<JWT>  → backend /validate-token se data + verifyId aata hai
//    (purane ?d=<XOR> links aur plain params bhi chalte rehte hain)

import { toPng } from "html-to-image";
import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { decryptParams, resolveCertificate, markDownloaded, timeLeftLabel } from "./encryptUrl";

import futureSkillsLogo from "./image/futureskills-img.png";
import googlePartnerLogo from "./image/google-partner-img.jpg";
import nasscomLogo from "./image/nasscom-img.png";
import skillIndiaLogo from "./image/skill-india-img.png";
import uncLogo from "./image/unc-img.png";
import goldAwardLogo from "./image/gold-award-img.png";
import gangeshSirSignature from "./image/gangeshSir.png";
import vijaySirSignature from "./image/vijaySir.png";

/* ── Date formatter: "2026-06-19" → "19th June 2026" ── */
function getOrdinal(n) {
  const num = Number(n);
  const s = ["th", "st", "nd", "rd"];
  const v = num % 100;
  const suffix = s[(v - 20) % 10] || s[v] || s[0];
  const padded = num < 10 ? `0${num}` : `${num}`;
  return padded + suffix;
}

function monthsToWord(m) {
  const map = {
    "1": "One", "1.5": "One & Half", "2": "Two", "2.5": "Two & Half",
    "3": "Three", "3.5": "Three & Half", "4": "Four", "4.5": "Four & Half",
    "5": "Five", "5.5": "Five & Half", "6": "Six", "6.5": "Six & Half",
    "7": "Seven", "7.5": "Seven & Half", "8": "Eight", "8.5": "Eight & Half",
    "9": "Nine", "9.5": "Nine & Half", "10": "Ten",
    "6-7": "Six to Seven", "8-9": "Eight to Nine",
  };
  return map[String(m).trim()] || m;
}

function formatDate(str) {
  if (!str) return "";
  const [y, m, d] = str.split("-");
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];
  return `${getOrdinal(d)} ${months[Number(m) - 1]} ${y}`;
}

export default function CertificatePage() {
  const [params] = useSearchParams();
  const certRef  = useRef(null);
  const qrRef    = useRef(null);

  const [loading, setLoading]   = useState(false);
  const [btnReady, setBtnReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [fail, setFail]         = useState(null);   // { code, message, portalUrl }
  const [cert, setCert]         = useState(null);   // { name, course, months, completionDate, verifyId }
  const [expiresAt, setExpires] = useState(null);

  const token       = params.get("token") || "";    // ✅ naya JWT link
  const legacyToken = params.get("d") || "";        // purana XOR link

  /* ── Certificate data laao ── */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setChecking(true);
      setFail(null);

      // 1) Naya JWT token — backend se verify karke data lo
      if (token) {
        const r = await resolveCertificate(token);
        if (cancelled) return;

        if (!r.ok) {
          setFail({ code: r.code, message: r.message, portalUrl: r.portalUrl });
        } else {
          setCert({
            name:           r.certificate.name || "",
            course:         r.certificate.course || "",
            months:         r.certificate.months || "",
            completionDate: r.certificate.completionDate || "",
            verifyId:       r.certificate.verifyId || "",   // ⭐ QR ke liye yahi chahiye
          });
          setExpires(r.expiresAt || null);
        }
        setChecking(false);
        return;
      }

      // 2) Purana XOR link
      if (legacyToken) {
        const d = decryptParams(legacyToken);
        if (cancelled) return;
        if (!d) {
          setFail({ code: "INVALID", message: "This certificate link is invalid or has expired." });
        } else {
          setCert({
            name:           d.name || "",
            course:         d.course || "",
            months:         d.months || "",
            completionDate: d.completionDate || d.date || "",
            verifyId:       d.verifyId || "",
          });
        }
        setChecking(false);
        return;
      }

      // 3) Plain params — admin testing ke liye
      if (cancelled) return;
      setCert({
        name:           params.get("name") || "",
        course:         params.get("course") || "",
        months:         params.get("months") || "",
        completionDate: params.get("completionDate") || params.get("date") || "",
        verifyId:       params.get("verifyId") || "",
      });
      setChecking(false);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, legacyToken]);

  const name    = cert?.name || "";
  const course  = cert?.course || "";
  const months  = cert?.months || "";
  const rawDate = cert?.completionDate || "";
  const verifyId = cert?.verifyId || "";

  const date = rawDate && rawDate.includes("-") ? formatDate(rawDate) : rawDate || "";

  /* ── QR — verifyId ke saath ── */
  const verifyUrl = verifyId ? `${window.location.origin}/verify/${verifyId}` : "";
  const qrSrc = verifyUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(verifyUrl)}`
    : "";

  /* QR load hote hi download button enable */
  useEffect(() => {
    setBtnReady(false);
    if (!qrSrc) { setBtnReady(true); return; }      // QR na ho to bhi button chale
    const img = qrRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) { setBtnReady(true); return; }
    img.onload  = () => setBtnReady(true);
    img.onerror = () => setBtnReady(true);
  }, [qrSrc]);

  /* ── PNG Download ── */
  const downloadPNG = useCallback(async () => {
    if (loading) return;
    try {
      setLoading(true);
      const dataUrl = await toPng(certRef.current, { cacheBust: true, pixelRatio: 3 });
      const link = document.createElement("a");
      link.download = `Certificate_${(name || "Student").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();

      // ✅ download count backend me record karo
      if (token) markDownloaded(token);
    } catch (err) {
      console.error(err);
      alert("Download failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [loading, name, token]);

  /* ── Loading screen ── */
  if (checking) {
    return (
      <CenterCard icon="⏳" title="Loading your certificate...">
        Please wait a moment.
      </CenterCard>
    );
  }

  /* ── Error screens ── */
  if (fail) {
    const titles = {
      EXPIRED:        "Download Link Expired",
      SUPERSEDED:     "A Newer Link Was Issued",
      REVOKED:        "Link Revoked",
      LIMIT_EXCEEDED: "Download Limit Reached",
      NOT_APPROVED:   "Certificate Not Available",
      NETWORK:        "Connection Problem",
    };
    const icons = {
      EXPIRED: "⏳", SUPERSEDED: "🔁", REVOKED: "🚫",
      LIMIT_EXCEEDED: "📥", NOT_APPROVED: "⚠️", NETWORK: "📡",
    };
    return (
      <CenterCard icon={icons[fail.code] || "🔒"} title={titles[fail.code] || "Invalid Certificate Link"}>
        {fail.message || "This certificate link is invalid or has expired."}
        {fail.portalUrl && (
          <>
            <br /><br />
            <a href={fail.portalUrl} style={{ color: "#f05a28", fontWeight: 700 }}>
              Go to the My Certificate page
            </a>
          </>
        )}
      </CenterCard>
    );
  }

  return (
    <>
      {/* ── Fonts + global styles ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&family=Montserrat:wght@400;500;600;700;800;900&display=swap');
        @page { size: landscape; margin: 0; }
        @media print {
          html, body { margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
        }
        .cert-page-body {
          font-family: 'Montserrat', sans-serif;
          background-color: #e5e7eb;
          margin: 0;
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 100vh;
          padding: 20px;
          flex-direction: column;
          gap: 20px;
        }
        .certificate-container {
          width: 1122px;
          height: 793px;
          background-color: #ffffff;
          position: relative;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }
        .watermark {
          position: absolute;
          top: 38%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 455px;
          height: 460px;
          opacity: 0.4;
          z-index: 0;
          pointer-events: none;
          filter: grayscale(100%) brightness(1);
        }
        .cert-head { margin-top: -70px; font-size: 60px; font-weight: 700; font-family: 'Montserrat', sans-serif; }
        .of-trn    { margin-top: -10px; font-size: 30px; font-weight: 600; font-family: 'Montserrat', sans-serif; }
        .iso       { margin-top: 6px; font-size: 24px; font-weight: 500; font-family: 'Montserrat', sans-serif; }
        .proudly   { margin-top: 8px; font-size: 22px; font-weight: 500; font-family: 'Montserrat', sans-serif; }
        .unc-mainlogo { height: 240px; margin-top: -30px; margin-left: -85px; }
        .course-comp { font-size: 18px; font-weight: bold; }
        .course-cert-date { font-size: 17px; font-weight: 600; }
        .founder-sign { margin-left: 20px; }
        .name-font { font-family: 'Great Vibes', cursive; }

        .dl-btn {
          background: #f05a28; color: white; border: none;
          padding: 14px 40px; border-radius: 10px;
          font-size: 15px; font-weight: 700; cursor: pointer;
          font-family: 'Montserrat', sans-serif;
          box-shadow: 0 4px 15px rgba(240,90,40,0.35);
          transition: all 0.2s; display: flex; align-items: center; gap: 8px;
        }
        .dl-btn:hover  { background: #d44a1e; transform: translateY(-1px); }
        .dl-btn:disabled { background: #aaa; cursor: not-allowed; transform: none; box-shadow: none; }

        .print-btn {
          background: #2d3542; color: white; border: none;
          padding: 14px 28px; border-radius: 10px;
          font-size: 15px; font-weight: 700; cursor: pointer;
          font-family: 'Montserrat', sans-serif; transition: all 0.2s;
        }
        .print-btn:hover { background: #1a2030; }

        .expiry-chip {
          background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
          padding: 8px 16px; border-radius: 20px; font-size: 13px; font-weight: 600;
        }

        .loader {
          border: 3px solid rgba(255,255,255,0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          width: 18px; height: 18px;
          animation: spin 0.8s linear infinite;
          display: inline-block;
        }
        @keyframes spin { to { transform: rotate(360deg); } }

        @media print {
          .cert-page-body {
            background-color: white; padding: 0; margin: 0;
            justify-content: flex-start; align-items: flex-start;
          }
          .certificate-container {
            box-shadow: none; width: 1122px; height: 793px; page-break-after: avoid;
          }
          .no-print { display: none !important; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      <div className="cert-page-body">

        {/* ── Controls ── */}
        <div className="no-print" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
          <button className="dl-btn" onClick={downloadPNG} disabled={loading || !btnReady}>
            {loading
              ? <><span className="loader" />&nbsp;Generating...</>
              : "⬇ Download Certificate (PNG)"}
          </button>
          <button className="print-btn" onClick={() => window.print()}>🖨 Print / PDF</button>
          {expiresAt && (
            <span className="expiry-chip">⏳ This link expires in {timeLeftLabel(expiresAt)}</span>
          )}
        </div>

        {/* verifyId missing ho to saaf warning — QR blank nahi jayega chupke se */}
        {!verifyId && (
          <div className="no-print" style={{
            background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626",
            padding: "10px 16px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          }}>
            ⚠️ Verification ID missing — the QR code cannot be generated. Please contact the administrator.
          </div>
        )}

        {/* ════ CERTIFICATE ════ */}
        <div className="certificate-container" ref={certRef}>

          <img src={goldAwardLogo} className="watermark" alt="" />

          <svg className="absolute top-0 right-0" style={{ width: 420, height: 420, zIndex: 1 }}
               viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon points="100,0 100,20 80,0" fill="#f05a28" />
            <path d="M25 0 L75 0 L100 25 L110 51 Q97 30 56 0 Z" fill="#2d3542" />
          </svg>

          <svg className="absolute bottom-0 left-0" style={{ width: 420, height: 420, zIndex: 1 }}
               viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon points="0,100 0,80 20,100" fill="#f05a28" />
            <path d="M0 25 L0 75 L25 100 L64 110 Q30 97 0 61 Z" fill="#2d3542" />
          </svg>

          <div className="absolute z-20" style={{ top: 50, left: 60 }}>
            <div style={{ width: 240, height: 70, display: "flex", alignItems: "center", justifyContent: "flex-start", padding: 4 }}>
              <img src={uncLogo} className="unc-mainlogo" alt="Main Logo"
                   onError={(e) => (e.target.style.display = "none")} />
            </div>
          </div>

          <div className="absolute w-full flex flex-col items-center" style={{ top: 75, zIndex: 10 }}>
            <h1 className="cert-head">CERTIFICATE</h1>
            <h2 className="of-trn">OF TRAINING</h2>
            <p className="iso">( ISO 9001 : 2016 Certified )</p>
            <p className="proudly">PROUDLY PRESENTED TO</p>

            <div className="mt-16 mb-2 inline-block text-center pb-1"
                 style={{ maxWidth: 900, borderBottom: "1px solid #d1d5db" }}>
              <div className="name-font text-center w-full"
                   style={{ fontSize: 85, color: "#2d3542", lineHeight: 0.5 }}>
                {name}
              </div>
            </div>

            <div className="mt-6 text-center" style={{ color: "#1e2736" }}>
              <p style={{ fontSize: 23, fontWeight: 600, letterSpacing: "0.05em", margin: 0 }}>
                This is to certify that You have successfully completed
              </p>
              <p style={{ fontSize: 23, fontWeight: 600, letterSpacing: "0.05em", margin: 0 }}>
                {months && course ? (
                  <>
                    <strong style={{ fontWeight: 700 }}>{monthsToWord(months)} Months</strong> Training in{" "}
                    <b style={{ fontWeight: 700 }}>{course}</b>.
                  </>
                ) : course ? (
                  <>Training in <b>{course}</b>.</>
                ) : null}
              </p>
            </div>

            <div className="flex items-center justify-center mt-10" style={{ gap: 32 }}>
              <span className="course-comp">Course Completion Date :</span>
              <span className="course-cert-date"
                    style={{ borderBottom: "3px solid #2d3542", paddingLeft: 24, paddingRight: 24 }}>
                <strong style={{ fontWeight: 700 }}>{date}</strong>
              </span>
            </div>
          </div>

          {/* Signatures & QR */}
          <div className="absolute w-full flex items-end"
               style={{
                 bottom: 140, paddingLeft: 160, paddingRight: 160,
                 justifyContent: "space-evenly", gap: 80, zIndex: 20, boxSizing: "border-box",
               }}>

            <div className="text-center" style={{ width: 240 }}>
              <div className="founder-sign">
                <img src={gangeshSirSignature}
                     style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain" }}
                     alt="gangesh sir" />
              </div>
              <div>
                <p style={{ fontWeight: 800, fontSize: 15, color: "#1e2736", letterSpacing: "0.05em", margin: "4px 0 0" }}>
                  Mr. Gangesh Kumar Gaunr
                </p>
                <p style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: "4px 0 0" }}>
                  Founder <br /> Uncodemy Edutech Pvt. Ltd.
                </p>
              </div>
            </div>

            {/* QR Code — ab verifyId ke saath */}
            <div className="flex flex-col items-center">
              <div style={{
                width: 110, height: 110, background: "#fff",
                border: "2px solid #f3f4f6",
                display: "flex", alignItems: "center", justifyContent: "center", padding: 2,
              }}>
                {qrSrc ? (
                  <img ref={qrRef} src={qrSrc} crossOrigin="anonymous"
                       style={{ width: "100%", height: "100%", objectFit: "contain" }}
                       alt="QR Code" />
                ) : null}
              </div>
            </div>

            <div className="text-center" style={{ width: 240 }}>
              <div style={{ height: 120, display: "flex", justifyContent: "center", alignItems: "flex-end", marginBottom: "-28px" }}>
                <img src={vijaySirSignature}
                     style={{ width: "69%", height: "auto", objectFit: "contain" }}
                     alt="vijay Sir" />
              </div>
              <div style={{ paddingTop: 8 }}>
                <p style={{ fontWeight: 800, fontSize: 15, color: "#1e2736", letterSpacing: "0.05em", margin: "4px 0 0" }}>
                  Dr. Vijay Kumar Gupta
                </p>
                <p style={{ fontSize: 14, fontWeight: 700, color: "#374151", margin: "4px 0 0" }}>
                  Managing Director &amp; CEO,<br /> Uncodemy Edutech Pvt. Ltd.
                </p>
              </div>
            </div>
          </div>

          {/* Footer Logos */}
          <div className="absolute w-full flex justify-center items-center"
               style={{ bottom: 40, gap: 4, marginLeft: 48, zIndex: 20 }}>
            <span style={{ fontWeight: 700, fontSize: 22, color: "#1e2736", marginRight: 4 }}>
              Certified By :
            </span>

            <div style={{ height: 60, width: 190, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
              <img src={skillIndiaLogo} style={{ height: 170, width: 190 }} alt="Skill India"
                   onError={(e) => (e.target.style.display = "none")} />
            </div>
            <div style={{ height: 60, width: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
              <img src={nasscomLogo} style={{ height: 75, width: 200 }} alt="NASSCOM"
                   onError={(e) => (e.target.style.display = "none")} />
            </div>
            <div style={{ height: 60, width: 190, display: "flex", alignItems: "center", justifyContent: "center", padding: "4px 4px 4px 28px" }}>
              <img src={googlePartnerLogo} style={{ height: 60, width: 190 }} alt="Google Partner"
                   onError={(e) => (e.target.style.display = "none")} />
            </div>
            <div style={{ height: 60, width: 190, display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
              <img src={futureSkillsLogo} style={{ height: 200, width: 190 }} alt="Future Skills"
                   onError={(e) => (e.target.style.display = "none")} />
            </div>
          </div>

        </div>{/* end certificate-container */}
      </div>{/* end cert-page-body */}
    </>
  );
}

/* ── Chhota reusable message card ── */
function CenterCard({ icon, title, children }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      fontFamily: "Montserrat, sans-serif", background: "#f3f4f6", padding: 16,
    }}>
      <div style={{
        background: "#fff", borderRadius: 16, padding: "48px 40px",
        boxShadow: "0 4px 24px rgba(0,0,0,0.10)", textAlign: "center", maxWidth: 460,
      }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>{icon}</div>
        <h2 style={{ color: "#1e2736", fontSize: 22, fontWeight: 800, margin: "0 0 10px" }}>
          {title}
        </h2>
        <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          {children}
        </p>
      </div>
    </div>
  );
}